import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Select, DatePicker, Table, Button } from "antd";
import moment from "moment";
import GYAxios from "../../../util/GYAxios";
import { CSVLink } from "react-csv";

const { Option } = Select;
const { MonthPicker } = DatePicker;

export default function SummaryReportRPRPython(props) {
  const [month, setMonth] = useState(moment());
  const [country, setCountry] = useState("TH");
  const [resolution, setResolution] = useState("monthly");
  const [selectedSite, setSelectedSite] = useState(null);
  const [data, setData] = useState([]);
  const [sites, setSites] = useState([]);

  const sitesFilter = useMemo(() => sites.filter(site => site.country === country), [sites, country]);

  const dataFilter = useMemo(() => selectedSite ? data.filter(row => row.site_id === selectedSite) : data, [ selectedSite, data]);

  const fetchData = useCallback(async () => {
    const endpoint = resolution === "daily" ? "daily" : "monthly";
    const params = {
      month: month.format("YYYYMM"),
      country,
    };
    const paramsString = new URLSearchParams(params).toString();
    const response = await GYAxios.get(`/rpr_python/${endpoint}?${paramsString}`, { timeout: 30000 });
    const mapData = (data) => data.map(row => ({
      ...row,
      dateData: moment(row.dateData).format("YYYY-MM-DD"),
    }));
    const mappedData = mapData(response.data);
    setData(mappedData);
  }, [ resolution, month, country ]);

  useEffect(() => {
    GYAxios.get("/select/sites").then((response) => {
      setSites(response.data);
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [ fetchData ]);

  useEffect(() => {
    setSelectedSite(null);
  }, [ month, country, resolution ])

  const columns = useMemo(() => {
    const suffix = resolution === "monthly" ? "_sum" : "";
    const columns = [
      { title: "SF Project ID", dataIndex: "SF_project_id", key: "SF_project_id" },
      { title: "Company", dataIndex: "company", key: "company" },
      { title: 'Site', dataIndex: 'site_name', key: 'site_name' },
      { title: 'Power', dataIndex: 'power', key: 'power' },
      { title: "RPR", dataIndex: `RPR`, key: `RPR` },
      { title: "On RPR", dataIndex: `on_RPR`, key: `on_RPR` },
      { title: "Off RPR", dataIndex: `off_RPR`, key: `off_RPR` },
      { title: "Net PR", dataIndex: `net_PR`, key: `net_PR` },
      { title: "RPR Solar", dataIndex: `RPR_solar${suffix}`, key: `RPR_solar${suffix}` },
      { title: "On RPR Solar", dataIndex: `on_RPR_solar${suffix}`, key: `on_RPR_solar${suffix}` },
      { title: "Off RPR Solar", dataIndex: `off_RPR_solar${suffix}`, key: `off_RPR_solar${suffix}` },
      { title: "RPR Irradiance", dataIndex: `RPR_irradiance${suffix}`, key: `RPR_irradiance${suffix}` },
      { title: "On RPR Irradiance", dataIndex: `on_RPR_irradiance${suffix}`, key: `on_RPR_irradiance${suffix}` },
      { title: "Off RPR Irradiance", dataIndex: `off_RPR_irradiance${suffix}`, key: `off_RPR_irradiance${suffix}` },
      { title: "Off Technical Solar", dataIndex: `off_technical_solar${suffix}`, key: `off_technical_solar${suffix}` },
      { title: "On Technical Solar", dataIndex: `on_technical_solar${suffix}`, key: `on_technical_solar${suffix}` },
      { title: "Off Technical Irradiance", dataIndex: `off_technical_irradiance${suffix}`, key: `off_technical_irradiance${suffix}` },
      { title: "On Technical Irradiance", dataIndex: `on_technical_irradiance${suffix}`, key: `on_technical_irradiance${suffix}` },
      { title: "On Technical PR", dataIndex: `on_technical_PR`, key: `on_technical_PR` },
      { title: "Off Technical PR", dataIndex: `off_technical_PR`, key: `off_technical_PR` },
      { title: "Technical PR", dataIndex: `technical_PR`, key: `technical_PR` },
      { title: "Actual On Solar", dataIndex: `actual_on_solar${suffix}`, key: `actual_on_solar${suffix}` },
      { title: "Actual Off Solar", dataIndex: `actual_off_solar${suffix}`, key: `actual_off_solar${suffix}` },
      { title: "Actual On Irradiance", dataIndex: `actual_on_irradiance${suffix}`, key: `actual_on_irradiance${suffix}` },
      { title: "Actual Off Irradiance", dataIndex: `actual_off_irradiance${suffix}`, key: `actual_off_irradiance${suffix}` },
      { title: "TA On Solar", dataIndex: `TA_on_solar${suffix}`, key: `TA_on_solar${suffix}` },
      { title: "TA Off Solar", dataIndex: `TA_off_solar${suffix}`, key: `TA_off_solar${suffix}` },
      { title: "TA On Irradiance", dataIndex: `TA_on_irradiance${suffix}`, key: `TA_on_irradiance${suffix}` },
      { title: "TA Off Irradiance", dataIndex: `TA_off_irradiance${suffix}`, key: `TA_off_irradiance${suffix}` },
      { title: "GA On Solar", dataIndex: `GA_on_solar${suffix}`, key: `GA_on_solar${suffix}` },
      { title: "GA Off Solar", dataIndex: `GA_off_solar${suffix}`, key: `GA_off_solar${suffix}` },
      { title: "GA On Irradiance", dataIndex: `GA_on_irradiance${suffix}`, key: `GA_on_irradiance${suffix}` },
      { title: "GA Off Irradiance", dataIndex: `GA_off_irradiance${suffix}`, key: `GA_off_irradiance${suffix}` },
      { title: "CA On Solar", dataIndex: `CA_on_solar${suffix}`, key: `CA_on_solar${suffix}` },
      { title: "CA Off Solar", dataIndex: `CA_off_solar${suffix}`, key: `CA_off_solar${suffix}` },
      { title: "CA On Irradiance", dataIndex: `CA_on_irradiance${suffix}`, key: `CA_on_irradiance${suffix}` },
      { title: "CA Off Irradiance", dataIndex: `CA_off_irradiance${suffix}`, key: `CA_off_irradiance${suffix}` },
      { title: "TPA On Solar", dataIndex: `TPA_on_solar${suffix}`, key: `TPA_on_solar${suffix}` },
      { title: "TPA Off Solar", dataIndex: `TPA_off_solar${suffix}`, key: `TPA_off_solar${suffix}` },
      { title: "TPA On Irradiance", dataIndex: `TPA_on_irradiance${suffix}`, key: `TPA_on_irradiance${suffix}` },
      { title: "TPA Off Irradiance", dataIndex: `TPA_off_irradiance${suffix}`, key: `TPA_off_irradiance${suffix}` },
      { title: "ZE On Solar", dataIndex: `ZE_on_solar${suffix}`, key: `ZE_on_solar${suffix}` },
      { title: "ZE Off Solar", dataIndex: `ZE_off_solar${suffix}`, key: `ZE_off_solar${suffix}` },
      { title: "ZE On Irradiance", dataIndex: `ZE_on_irradiance${suffix}`, key: `ZE_on_irradiance${suffix}` },
      { title: "ZE Off Irradiance", dataIndex: `ZE_off_irradiance${suffix}`, key: `ZE_off_irradiance${suffix}` },
      { title: "Ambient Temp", dataIndex: `avg_temp_ambient`, key: `avg_temp_ambient` },
      { title: "Invoice Solar Total", dataIndex: `invc_solar_total${suffix}`, key: `invc_solar_total${suffix}` },
      { title: "Invoice Solar On", dataIndex: `invc_solar_on${suffix}`, key: `invc_solar_on${suffix}` },
      { title: "Invoice Solar Off", dataIndex: `invc_solar_off${suffix}`, key: `invc_solar_off${suffix}` },
      { title: "Invoice Irradiance Total", dataIndex: `invc_irr_total${suffix}`, key: `invc_irr_total${suffix}` },
      { title: "Invoice Irradiance On", dataIndex: `invc_irr_on${suffix}`, key: `invc_irr_on${suffix}` },
      { title: "Invoice Irradiance Off", dataIndex: `invc_irr_off${suffix}`, key: `invc_irr_off${suffix}` },
    ];
    // add dateData columns in daily resolution
    if(resolution === 'daily') {
      columns.unshift({ title: "Date", dataIndex: "dateData", key: "dateData" });
      columns.push({ title: "Updated Daily", dataIndex: "updated_daily", key: "updated" });
      columns.push({ title: "Corrected Irr", dataIndex: "corrected_irradiance", key: "corrected_irradiance" });
    }
    return columns;
  }, [  resolution ]);

  return (
    <div style={{padding: '1em'}}>
      <div style={{ display: 'flex', gap: '1em', height: '32px', marginBottom: '1em' }}>
        <MonthPicker
          value={month}
          onChange={(date) => setMonth(date)}
          format="YYYY-MM"
          allowClear={false}
        />
        <Select value={country} onChange={setCountry} style={{ width: 120 }}>
          <Option value="TH">Thailand</Option>
          <Option value="VN">Vietnam</Option>
          <Option value="KH">Cambodia</Option>
          <Option value="PH">Philippines</Option>
        </Select>
        <Select value={resolution} onChange={setResolution} style={{ width: 120 }}>
          <Option value="daily">Daily</Option>
          <Option value="monthly">Monthly</Option>
        </Select>
        {resolution === "daily" && (
          <Select
          value={selectedSite}
          onChange={setSelectedSite}
          style={{ width: 200 }}
          placeholder="Select a site"
          showSearch
          allowClear
          filterOption={(input, option) =>
            (option?.children.filter(elm => elm).join(' ') ?? '').toLowerCase().includes(input.toLowerCase())
          }
          >
            {sitesFilter.map((site) => (
              <Option key={site.value} value={site.value}>
                {site.company} {site.site_name}
              </Option>
            ))}
          </Select>
        )}
        <CSVLink data={dataFilter} filename={`summary_report_${resolution}_${month.format("YYYYMM")}.csv`}>
          <Button type="primary" style={{ marginBottom: 16 }}>
            Download CSV
          </Button>
        </CSVLink>
        </div>
      <Table
        columns={columns}
        dataSource={dataFilter}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}
